/**
 * Portal Invitation Dialog
 *
 * Shown after a contract is fully signed. Simulates sending email + SMS
 * portal invitations to the homeowner with a confirmation summary.
 */

import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Mail, Phone, CheckCircle, Send, User, ExternalLink, Sparkles,
} from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  jobId?: string;
  portalUrl?: string;
}

export function PortalInvitationDialog({
  open, onOpenChange, customerName, customerEmail, customerPhone, jobId, portalUrl,
}: Props) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const firstName = (customerName || "").split(" ")[0] || "Customer";
  const finalPortalUrl = portalUrl || `/portal/login?token=${jobId}&setup=true`;

  const handleSendInvitation = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);

      if (customerEmail) {
        toast.success(`Email invitation sent to ${customerEmail}`, {
          description: "Portal setup link included",
          icon: <Mail className="h-4 w-4" />,
        });
      }
      if (customerPhone) {
        setTimeout(() => {
          toast.success(`SMS invitation sent to ${customerPhone}`, {
            description: "Portal access link included",
            icon: <Phone className="h-4 w-4" />,
          });
        }, 400);
      }
    }, 1200);
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset after animation
    setTimeout(() => setSent(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            {sent ? "Invitation Sent!" : "Invite to Customer Portal"}
          </DialogTitle>
          <DialogDescription>
            {sent
              ? `${firstName} has been invited to set up their Customer Portal account.`
              : `Send ${firstName} a portal invitation so they can track their project online.`
            }
          </DialogDescription>
        </DialogHeader>

        {!sent ? (
          <div className="space-y-4 py-2">
            {/* Customer info */}
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <User className="h-4 w-4 text-muted-foreground" />
                {customerName}
              </div>
              <div className="space-y-1.5 ml-6">
                {customerEmail && (
                  <div className="flex items-center gap-2 text-xs">
                    <Mail className="h-3 w-3 text-muted-foreground" />
                    <span>{customerEmail}</span>
                    <Badge variant="outline" className="text-[9px] h-4 bg-success/5 text-success border-success/30">
                      Will Send
                    </Badge>
                  </div>
                )}
                {customerPhone && (
                  <div className="flex items-center gap-2 text-xs">
                    <Phone className="h-3 w-3 text-muted-foreground" />
                    <span>{customerPhone}</span>
                    <Badge variant="outline" className="text-[9px] h-4 bg-success/5 text-success border-success/30">
                      Will Send
                    </Badge>
                  </div>
                )}
                {!customerEmail && !customerPhone && (
                  <p className="text-xs text-muted-foreground italic">No contact info available</p>
                )}
              </div>
            </div>

            {/* What they'll receive */}
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em]">Invitation Includes</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
                  <span>Congratulations message confirming their signed agreement</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
                  <span>Secure link to set up their portal account</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
                  <span>Access to signed contract, project details, and updates</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-4 space-y-4">
            {/* Success state */}
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-7 w-7 text-success" />
              </div>
            </div>

            {/* Delivery summary */}
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
              {customerEmail && (
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Email to {customerEmail}</span>
                  </div>
                  <Badge className="text-[9px] h-4 bg-success/10 text-success border-0">Sent</Badge>
                </div>
              )}
              {customerPhone && (
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>SMS to {customerPhone}</span>
                  </div>
                  <Badge className="text-[9px] h-4 bg-success/10 text-success border-0">Sent</Badge>
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              {firstName} will receive a link to create their account and view their project.
            </p>
          </div>
        )}

        <DialogFooter>
          {!sent ? (
            <>
              <Button variant="outline" size="sm" onClick={handleClose}>
                Skip for Now
              </Button>
              <Button
                size="sm"
                onClick={handleSendInvitation}
                disabled={sending || (!customerEmail && !customerPhone)}
              >
                {sending ? "Sending…" : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    Send Invitation
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={handleClose}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
